import React, { useState, useEffect, useContext } from 'react';
import { Grid, FormLabel, FormHelperText, CircularProgress } from '@material-ui/core';
import { TextBox, SelectBox, DatePicker, SecondaryButton } from '../../../../Core/FormInput';
import { postWorkshopShiftAddHolidayAndRemoveAssignments } from '../../../../Core/Service/WorkshopShiftSchedule-service';
import { getTCardUserHolidayDropdown } from '../../../../Core/Service/TCard-UserHoliday-service';
import AppContext from '../../../../App/AppContext';
import moment from 'moment';

const AddHolidayModal = (props) => {
    const { onClose, shiftID, shiftName, branchName, branchID, defaultStartDate, defaultEndDate, existingAssignment, technicianName } = props;
    const { showToast } = useContext(AppContext);

    const [state, setState] = useState({
        startDate: '',
        endDate: '',
        holidayTypeID: '',
        holidayNotes: ''
    });
    const [holidayTypeList, setHolidayTypeList] = useState([]);
    const [errors, setErrors] = useState({});
    const [isBtnLoader, setIsBtnLoader] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchHolidayTypes = async () => {
            try {
                const res = await getTCardUserHolidayDropdown();
                if (res.success && Array.isArray(res.data?.holidayType)) {
                    setHolidayTypeList(res.data.holidayType);
                } else {
                    showToast('Failed to load holiday types', 'error');
                }
            } catch (err) {
                showToast('Error loading holiday types', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchHolidayTypes();

        setState({
            startDate: defaultStartDate ? moment(defaultStartDate).format('YYYY-MM-DD') : '',
            endDate: defaultEndDate ? moment(defaultEndDate).format('YYYY-MM-DD') : '',
            holidayTypeID: '',
            holidayNotes: ''
        });
    }, [defaultStartDate, defaultEndDate]);

    const handleInput = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((err) => ({ ...err, [name]: null }));
        }
    };

    const validate = () => {
        const tempErrors = {};
        if (!state.startDate) tempErrors.startDate = 'Start Date is required';
        if (!state.endDate) tempErrors.endDate = 'End Date is required';
        if (state.startDate && state.endDate && state.startDate > state.endDate) {
            tempErrors.endDate = 'End Date must be after Start Date';
        }
        if (!state.holidayTypeID) tempErrors.holidayTypeID = 'Holiday Type is required';
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsBtnLoader(true);
        try {
            const payload = {
                ShiftID: shiftID,
                StartDate: moment.utc(state.startDate).startOf('day').toISOString(),
                EndDate: moment.utc(state.endDate).startOf('day').toISOString(),
                TechnicianUserID: existingAssignment?.technicianUserID,
                BranchID: branchID ? parseInt(branchID, 10) : null,
                HolidayTypeID: parseInt(state.holidayTypeID, 10),
                HolidayNotes: state.holidayNotes || ''
            };

            const res = await postWorkshopShiftAddHolidayAndRemoveAssignments(payload);
            if (res.success || res.data?.success) {
                showToast(res.data?.message || res.message || 'Holiday added and assignments removed successfully', 'success');
                onClose(true);
            } else {
                showToast(res.data?.message || res.message || 'Failed to add holiday and remove assignments', 'error');
            }
        } catch (error) {
            console.error('Error adding holiday:', error);
            showToast('An error occurred while adding holiday', 'error');
        } finally {
            setIsBtnLoader(false);
        }
    };

    if (isLoading) {
        return (
            <Grid container justify="center" alignContent="center" alignItems="center" style={{ minHeight: '180px' }}>
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid container spacing={2} style={{ padding: '8px' }}>
            {/* ── Context Info ── */}
            {(branchName || shiftName || technicianName) && (
                <Grid item xs={12}>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            paddingBottom: 8,
                            borderBottom: '1px solid #E5E7EB',
                            marginBottom: 4,
                            justifyContent: 'space-between'
                        }}
                    >
                        {branchName && (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Branch</span>
                                <strong style={{ fontSize: 15, color: '#111827', marginTop: 2 }}>{branchName}</strong>
                            </div>
                        )}
                        {shiftName && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Shift</span>
                                <strong style={{ fontSize: 15, color: '#111827', marginTop: 2 }}>{shiftName}</strong>
                            </div>
                        )}
                        {technicianName && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Technician</span>
                                <strong style={{ fontSize: 15, color: '#111827', marginTop: 2 }}>{technicianName}</strong>
                            </div>
                        )}
                    </div>
                </Grid>
            )}

            <Grid item xs={12} sm={6}>
                <FormLabel required className="mandatoryfields">
                    Start Date
                </FormLabel>
                <DatePicker name="startDate" value={state.startDate} onChange={handleInput} fullWidth />
                {errors.startDate && <FormHelperText error>{errors.startDate}</FormHelperText>}
            </Grid>

            <Grid item xs={12} sm={6}>
                <FormLabel required className="mandatoryfields">
                    End Date
                </FormLabel>
                <DatePicker name="endDate" value={state.endDate} onChange={handleInput} fullWidth />
                {errors.endDate && <FormHelperText error>{errors.endDate}</FormHelperText>}
            </Grid>

            <Grid item xs={12}>
                <FormLabel required className="mandatoryfields">
                    Holiday Type
                </FormLabel>
                <SelectBox name="holidayTypeID" value={state.holidayTypeID} onChange={handleInput} List={holidayTypeList} insertEmpty={true} fullWidth />
                {errors.holidayTypeID && <FormHelperText error>{errors.holidayTypeID}</FormHelperText>}
            </Grid>

            <Grid item xs={12}>
                <FormLabel component="legend">Notes</FormLabel>
                <TextBox name="holidayNotes" value={state.holidayNotes} onChange={handleInput} multiline rows={3} fullWidth />
            </Grid>

            <Grid item xs={12} container justify="flex-end" spacing={1} style={{ marginTop: '16px' }}>
                <Grid item xs={6}>
                    <SecondaryButton fullWidth onClick={() => onClose(false)}>
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid item xs={6}>
                    <SecondaryButton fullWidth onClick={handleSubmit} isBtnLoader={isBtnLoader || isLoading}>
                        Save
                    </SecondaryButton>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AddHolidayModal;
